import { User } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DriveFolder, DriveSearchResult } from '../types';

/**
 * Google Drive Service
 * Uses Firebase Functions with service account for server-side API calls.
 * No client-side OAuth tokens required.
 */
export class GoogleDriveService {
  private static instance: GoogleDriveService;
  private functions = getFunctions();

  static getInstance(): GoogleDriveService {
    if (!GoogleDriveService.instance) {
      GoogleDriveService.instance = new GoogleDriveService();
    }
    return GoogleDriveService.instance;
  }

  /**
   * Search for Drive folders via Firebase Functions
   * Uses service account with domain-wide delegation - no client OAuth needed
   */
  async searchFolders(query: string, _firebaseUser: User, pageToken?: string, parentFolderId?: string): Promise<DriveSearchResult> {
    try {
      console.log(`🔍 Searching Drive folders via Firebase Functions: "${query}"`);
      
      const searchDriveFoldersFunc = httpsCallable(this.functions, 'searchDriveFolders');
      const response = await searchDriveFoldersFunc({ 
        query, 
        pageToken, 
        maxResults: 20,
        parentFolderId 
      });
      
      const result = response.data as { 
        success: boolean; 
        data?: { folders: DriveFolder[]; nextPageToken?: string }; 
        message?: string 
      };
      
      if (!result.success || !result.data) {
        console.log(`ℹ️ No folders found for query: "${query}"`);
        return { folders: [] };
      }

      console.log(`✅ Found ${result.data.folders.length} folders for query: "${query}"`);

      return {
        folders: result.data.folders,
        nextPageToken: result.data.nextPageToken
      };

    } catch (error: any) {
      console.error('❌ Error searching Drive folders:', error);
      
      // Handle Firebase Functions errors
      if (error.code === 'functions/permission-denied') {
        throw new Error('Insufficient permissions. Google Drive access required.');
      } else if (error.code === 'functions/unauthenticated') {
        throw new Error('Authentication required. Please sign in to access Google Drive.');
      } else {
        throw new Error(`Failed to search Drive folders: ${error.message || 'Unknown error'}`);
      }
    }
  }

  /**
   * Get recent Drive folders via Firebase Functions
   * Uses service account with domain-wide delegation - no client OAuth needed
   */
  async getRecentFolders(_firebaseUser: User, maxResults: number = 10): Promise<DriveFolder[]> {
    try {
      console.log('📂 Getting recent Drive folders via Firebase Functions...');
      
      const getRecentDriveFoldersFunc = httpsCallable(this.functions, 'getRecentDriveFolders');
      const response = await getRecentDriveFoldersFunc({ maxResults });
      
      const result = response.data as { success: boolean; data?: DriveFolder[]; message?: string };
      
      if (!result.success || !result.data) {
        console.log('ℹ️ No recent folders found');
        return [];
      }

      console.log(`✅ Found ${result.data.length} recent folders`);
      return result.data;

    } catch (error: any) {
      console.error('❌ Error getting recent Drive folders:', error);
      
      if (error.code === 'functions/permission-denied') {
        throw new Error('Insufficient permissions. Google Drive access required.');
      } else {
        throw new Error(`Failed to get recent folders: ${error.message || 'Unknown error'}`);
      }
    }
  }

  /**
   * Validate a Drive folder URL via Firebase Functions
   * Uses service account with domain-wide delegation - no client OAuth needed
   */
  async validateFolderUrl(url: string): Promise<{ valid: boolean; folder?: DriveFolder }> {
    try {
      console.log(`🔍 Validating Drive folder URL via Firebase Functions: ${url}`);
      
      const validateDriveFolderUrlFunc = httpsCallable(this.functions, 'validateDriveFolderUrl');
      const response = await validateDriveFolderUrlFunc({ url });
      
      const result = response.data as { 
        success: boolean; 
        data?: { valid: boolean; folder?: DriveFolder; error?: string }; 
        message?: string 
      };
      
      if (!result.success || !result.data) {
        console.log('❌ Failed to validate Drive folder URL');
        return { valid: false };
      }

      if (!result.data.valid) {
        console.log(`❌ Invalid Drive folder URL: ${result.data.error || 'Unknown reason'}`);
        return { valid: false };
      }

      console.log(`✅ Valid Drive folder: ${result.data.folder?.name}`);
      
      return {
        valid: true,
        folder: result.data.folder
      };

    } catch (error: any) {
      console.error('❌ Error validating Drive folder URL:', error);
      
      // Return invalid for most errors (permission, not found, etc.)
      return { valid: false };
    }
  }

  /**
   * Extract folder ID from a Google Drive URL
   */
  extractFolderIdFromUrl(url: string): string | null {
    try {
      // Handle different Google Drive URL formats
      const patterns = [
        /\/folders\/([a-zA-Z0-9-_]+)/, // Standard folder URL
        /[?&]id=([a-zA-Z0-9-_]+)/, // Query parameter format
        /\/drive\/folders\/([a-zA-Z0-9-_]+)/, // Alternative format
        /\/drive\/u\/\d+\/folders\/([a-zA-Z0-9-_]+)/ // User-specific format like /drive/u/0/folders/ID
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          return match[1];
        }
      }

      return null;
    } catch (error) {
      console.error('❌ Error extracting folder ID from URL:', error);
      return null;
    }
  }

  /**
   * Search within the Jobs folder specifically
   */
  async searchInJobsFolder(query: string, firebaseUser: User, pageToken?: string): Promise<DriveSearchResult> {
    const jobsFolderId = '0ANoyp9lyuvgvUk9PVA'; // Jobs folder ID from the provided URL
    console.log(`🔍 Searching in Jobs folder for: "${query}"`);
    return this.searchFolders(query, firebaseUser, pageToken, jobsFolderId);
  }

  /**
   * Check if a URL is a valid Google Drive URL format
   */
  isValidGoogleDriveUrl(url: string): boolean {
    try {
      // Check if URL looks like a Google Drive URL
      const driveUrlPattern = /https:\/\/drive\.google\.com/;
      const folderPattern = /\/folders\/[a-zA-Z0-9-_]+/;
      
      return driveUrlPattern.test(url) && (folderPattern.test(url) || url.includes('id='));
    } catch (error) {
      console.error('❌ Error validating Google Drive URL format:', error);
      return false;
    }
  }
}
